<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <!-- Header -->
      <div class="text-center">
        <h2 class="mt-6 text-3xl font-bold text-gray-900">
          ログイン認証
        </h2>
        <p class="mt-2 text-sm text-gray-600">
          メールアドレスを入力して認証コードを受け取ってください
        </p>
        <p class="mt-1 text-sm text-gray-600">
          新規ユーザーも既存ユーザーも同じ認証フローでログインできます
        </p>
      </div>

      <!-- OTP Request Form -->
      <form class="mt-8 space-y-6" @submit.prevent="handleSubmit">
        <div class="space-y-4">
          <!-- Email Field -->
          <AppInput
            v-model="email"
            type="email"
            label="メールアドレス"
            placeholder="your@email.com"
            :error="emailError"
            :disabled="loading"
            autocomplete="email"
            required
            @blur="validateEmail"
          />

          <!-- Authentication Info -->
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                </svg>
              </div>
              <div class="ml-3">
                <div class="text-sm text-blue-700">
                  <p>パスワードは不要です。メールアドレスのみで認証を行います。</p>
                  <ul class="list-disc list-inside mt-2">
                    <li>6桁の認証コードをメールで送信</li>
                    <li>または、メール内のワンクリックリンクで認証</li>
                    <li>より安全で簡単なログイン方式</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div>
          <AppButton
            type="submit"
            variant="primary"
            size="lg"
            :loading="loading"
            :disabled="!isFormValid || loading"
            full-width
          >
            認証コードを送信
          </AppButton>
        </div>

        <!-- Error Message -->
        <div v-if="errorMessage" class="alert-danger">
          {{ errorMessage }}
        </div>
      </form>

      <!-- Footer Links -->
      <div class="text-center text-sm text-gray-600">
        <a
          href="/"
          class="font-medium text-primary-600 hover:text-primary-500"
        >
          ← ホームに戻る
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '../../stores/auth.js'
import AppInput from '../../components/ui/AppInput.vue'
import AppButton from '../../components/ui/AppButton.vue'

const router = useRouter()
const route = useRoute()
const toast = useToast()
const authStore = useAuthStore()

const email = ref('')
const emailError = ref('')
const errorMessage = ref('')
const loading = ref(false)

const isFormValid = computed(() => {
  return email.value && !emailError.value
})

const validateEmail = () => {
  if (!email.value) {
    emailError.value = 'メールアドレスを入力してください'
    return false
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email.value)) {
    emailError.value = '有効なメールアドレスを入力してください'
    return false
  }

  emailError.value = ''
  return true
}

const handleSubmit = async () => {
  if (!validateEmail()) {
    return
  }

  loading.value = true
  errorMessage.value = ''

  try {
    await authStore.requestOtp({ email: email.value })

    toast.success('認証コードを送信しました。メールをご確認ください。')

    // OTP認証ページに遷移（emailをクエリパラメータとして渡す）
    router.push({
      path: '/auth/verify-otp',
      query: {
        email: email.value,
        redirect: route.query.redirect || '/dashboard'
      }
    })
  } catch (error) {
    console.error('OTP Request error:', error)
    errorMessage.value = error.message || 'OTPリクエストに失敗しました'
    toast.error(errorMessage.value)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  // Check if user is already logged in
  if (authStore.isAuthenticated) {
    router.push('/dashboard')
  }
})
</script>
